<!-- Previous calendar month: daily bookings for one selected location. -->
<template>
    <section class="last-month-report">
        <header class="last-month-header">
            <h2>Last month overview</h2>
            <p class="last-month-subtitle">
                <template v-if="applicable">
                    {{ periodLabel }} · daily bookings for the selected location.
                </template>
                <template v-else>
                    Select one location in the filter above to see last month per location.
                </template>
            </p>
        </header>

        <template v-if="applicable">
            <p v-if="loading" class="last-month-status">Loading last month data…</p>
            <p v-else-if="error" class="last-month-status last-month-status--error">{{ error }}</p>

            <template v-else-if="ready">
                <section class="summary-grid">
                    <article class="summary-card">
                        <h3>Total bookings</h3>
                        <p class="summary-value">{{ summary.yourTotal }}</p>
                        <p class="summary-sub">{{ summary.yourAvgPerDay }} / day avg</p>
                    </article>
                    <article class="summary-card">
                        <h3>Avg rating</h3>
                        <p class="summary-value">{{ formatRating(summary.avgRating) }}</p>
                    </article>
                    <article class="summary-card">
                        <h3>Avg location rating</h3>
                        <p class="summary-value">{{ formatRating(summary.avgLocationRating) }}</p>
                    </article>
                </section>

                <dashboard-panel
                    title="Bookings per day"
                    :subtitle="periodLabel"
                >
                    <dashboard-bar-chart-host :labels="labels" :datasets="datasets" />
                </dashboard-panel>
            </template>
        </template>
    </section>
</template>

<script setup>
import DashboardBarChartHost from "./DashboardBarChartHost.vue";
import DashboardPanel from "./DashboardPanel.vue";

defineProps({
    applicable: { type: Boolean, default: false },
    ready: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    error: { type: String, default: "" },
    periodLabel: { type: String, default: "" },
    labels: { type: Array, default: () => [] },
    datasets: { type: Array, default: () => [] },
    summary: {
        type: Object,
        default: () => ({
            yourTotal: 0,
            yourAvgPerDay: 0,
            avgRating: 0,
            avgLocationRating: 0,
        }),
    },
});

function formatRating(value) {
    const rating = Number(value) || 0;
    return rating > 0 ? rating.toFixed(1) : "N/A";
}
</script>

<style scoped lang="scss">
@use "../../colors" as *;

.last-month-report {
    margin-top: 2.5rem;
    padding-top: 1.5rem;
    border-top: 2px solid rgba(255, 255, 255, 0.2);
}

.last-month-header {
    margin-bottom: 1rem;

    h2 {
        margin: 0 0 0.35rem;
        font-size: 1.35rem;
        color: $color-primary;
    }
}

.last-month-subtitle {
    margin: 0;
    color: $color-text-primary;
    font-size: 0.95rem;
    max-width: 52rem;
}

.last-month-status {
    margin: 0.5rem 0 1rem;
    color: $color-text-primary;
    font-size: 0.95rem;

    &--error {
        color: $color-danger;
    }
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
    gap: 0.75rem;
    margin-bottom: 1rem;
}

.summary-card {
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 0.5rem;
    background: rgba(255, 255, 255, 0.04);
    padding: 1rem;

    h3 {
        margin: 0 0 0.5rem;
        font-size: 0.85rem;
        font-weight: 500;
        color: $color-text-primary;
        display: inline-flex;
        align-items: center;
    }
}

.summary-value {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 700;
    color: #fff;
}

.summary-sub {
    margin: 0.35rem 0 0;
    font-size: 0.8rem;
    color: $color-text-regular;
}
</style>
